import {
  Badge,
  Body1,
  Button,
  Field,
  Input,
  Spinner,
  Subtitle2,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { SHRINKABLE } from '../responsive';

import { useTimeAdminApi } from './api';
import type { UserEmployeeLink } from './api';
import { DataTable } from './components/DataTable';
import type { DataColumn } from './components/DataTable';
import { SideBlade } from './components/SideBlade';
import { toErrorMessage } from './errors';
import { linkPayload } from './supervisorApproval';

const useStyles = makeStyles({
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    // Every ancestor between the page and a table's scroller must be able to
    // shrink, or the overflow lands on the document instead. See responsive.ts.
    ...SHRINKABLE,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalM,
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
  },
});

/**
 * The Entra LOGIN → employee LINK grid: which NetSuite employee each signed-in
 * identity resolves to. This is the always-available fallback for when a person's
 * NetSuite email differs from their Entra UPN, so the email auto-link misses and
 * they would otherwise have no reports to approve.
 *
 * Extracted verbatim from the retired `EmployeeMappingTab` when the Employees
 * roster absorbed that tab, so that this surface was CARRIED OVER rather than
 * dropped — it is the only place a manual link can be set.
 *
 * Admin-only (the underlying endpoints are Admin-gated server-side).
 * PII: linkage ids + email only, which the API already returns.
 */
export function EntraLinksSection(): JSX.Element {
  const styles = useStyles();
  const api = useTimeAdminApi();

  const [userLinks, setUserLinks] = useState<UserEmployeeLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [target, setTarget] = useState<UserEmployeeLink | null>(null);
  const [nsId, setNsId] = useState('');
  const [saving, setSaving] = useState(false);
  const [bladeError, setBladeError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    void api
      .listUserLinks()
      .then((links) => {
        if (active) {
          setUserLinks(links);
        }
      })
      .catch((err: unknown) => {
        if (active) {
          setError(toErrorMessage(err));
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [api]);

  const openEdit = useCallback((u: UserEmployeeLink) => {
    setTarget(u);
    setNsId(u.nsEmployeeId ?? '');
    setBladeError(null);
  }, []);

  const closeEdit = useCallback(() => {
    setTarget(null);
    setNsId('');
    setBladeError(null);
    setSaving(false);
  }, []);

  const handleSave = useCallback(async () => {
    if (target === null) {
      return;
    }
    setSaving(true);
    setBladeError(null);
    const body = linkPayload(nsId);
    try {
      // The PATCH returns `{ ok: true }` (no row echoed) — reflect the submitted
      // value optimistically rather than from a returned row.
      await api.setUserEmployeeLink(target.userId, body);
      setUserLinks((prev) =>
        prev.map((u) =>
          u.userId === target.userId
            ? {
                ...u,
                nsEmployeeId: body.nsEmployeeId,
                linkSource: body.nsEmployeeId === null ? null : 'admin_manual',
              }
            : u
        )
      );
      closeEdit();
    } catch (err: unknown) {
      setBladeError(toErrorMessage(err));
      setSaving(false);
    }
  }, [api, target, nsId, closeEdit]);

  const columns: ReadonlyArray<DataColumn<UserEmployeeLink>> = useMemo(
    () => [
      {
        columnId: 'displayName',
        header: 'Sign-in',
        renderCell: (u) => u.displayName ?? u.email ?? u.externalId,
      },
      { columnId: 'email', header: 'Email', renderCell: (u) => u.email ?? '—' },
      {
        columnId: 'nsEmployeeId',
        header: 'Linked NetSuite id',
        renderCell: (u) => u.nsEmployeeId ?? '—',
      },
      {
        columnId: 'source',
        header: 'Link',
        renderCell: (u) =>
          u.nsEmployeeId === null ? (
            <Badge appearance="tint" color="warning">
              Unlinked
            </Badge>
          ) : (
            <Badge appearance="tint" color="success">
              {u.linkSource === 'admin_manual' ? 'Manual' : 'Auto (email)'}
            </Badge>
          ),
      },
      {
        columnId: 'action',
        header: '',
        renderCell: (u) => (
          <Button size="small" onClick={() => openEdit(u)}>
            {u.nsEmployeeId === null ? 'Link' : 'Edit'}
          </Button>
        ),
      },
    ],
    [openEdit]
  );

  return (
    <div className={styles.section}>
      <Subtitle2>Entra login links</Subtitle2>
      <Body1>
        Set the NetSuite employee each signed-in user maps to. Use this when a user’s NetSuite email
        differs from their sign-in address, so email auto-link can’t resolve them.
      </Body1>

      {error !== null ? (
        <Text className={styles.error} role="alert">
          {error}
        </Text>
      ) : null}

      {loading ? (
        <Spinner label="Loading logins…" />
      ) : (
        <DataTable
          columns={columns}
          rows={userLinks}
          getRowId={(u) => u.userId}
          emptyMessage="No Entra logins to show yet."
        />
      )}

      <SideBlade
        open={target !== null}
        onClose={closeEdit}
        title={
          target === null
            ? ''
            : `Employee link — ${target.displayName ?? target.email ?? target.externalId}`
        }
        size="small"
      >
        {target !== null ? (
          <form
            className={styles.form}
            onSubmit={(ev) => {
              ev.preventDefault();
              void handleSave();
            }}
          >
            <Field
              label="NetSuite employee id"
              hint="Enter the NS employee internal id this login maps to, or leave blank to clear the link (unlink)."
            >
              <Input value={nsId} onChange={(_, d) => setNsId(d.value)} disabled={saving} />
            </Field>
            {bladeError !== null ? (
              <Text className={styles.error} role="alert">
                {bladeError}
              </Text>
            ) : null}
            <div className={styles.actions}>
              <Button appearance="secondary" onClick={closeEdit} disabled={saving}>
                Cancel
              </Button>
              <Button appearance="primary" type="submit" disabled={saving}>
                {saving ? <Spinner size="tiny" /> : 'Save link'}
              </Button>
            </div>
          </form>
        ) : null}
      </SideBlade>
    </div>
  );
}
